import React, { useEffect, useState } from 'react';
import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import moment, { Moment } from 'moment';
import 'react-dates/lib/css/_datepicker.css';
import classNames from 'classnames';
import { useResponsive } from 'helpers/use_responsive';

interface DateInputRangeProps {
  startDate?: string;
  endDate?: string;
  onChange: (value: any) => void;
  disabled?: boolean;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  minDate?: string;
  maxDate?: string;
  hideClearDates?: boolean;
  appendToBody?: boolean;
  fullWidth?: boolean;
  variant?: '36';
}

const DateInputRange = ({
  startDate,
  endDate,
  onChange,
  disabled,
  minDate,
  maxDate,
  hideClearDates,
  appendToBody,
  variant,
  fullWidth,
  startDatePlaceholder,
  endDatePlaceholder,
}: DateInputRangeProps) => {
  const [id] = useState(Math.floor(Math.random() * 100000).toString());
  const [focusedInput, setFocusedInput] = useState(null);
  const [keepFocused, setKeepFocused] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (focusedInput) {
      window.addEventListener('mousedown', onMouseDown);
    }

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [focusedInput]);

  const onMouseDown = (event) => {
    const isScrollbarClick = window.innerWidth - event.clientX < 16;

    setKeepFocused(!!isScrollbarClick);
  };

  const parseDate = (date) => {
    if (!date) return date;

    return moment(date).format('YYYY-MM-DD');
  };

  const _onChange = ({ startDate, endDate }) => {
    if (disabled) return;

    onChange({ startDate: parseDate(startDate), endDate: parseDate(endDate) });
  };

  const getStartValue = () => {
    if (!startDate) return null;

    return moment(startDate);
  };

  const getEndValue = () => {
    if (!endDate) return null;

    return moment(endDate);
  };

  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => {
    let i;
    const years = [];
    for (i = moment().year() + 10; i >= moment().year() - 100; i--) {
      years.push(
        <option value={i} key={`year-${i}`}>
          {i}
        </option>,
      );
    }
    return (
      <div className="datepicker__month-select">
        <div>
          <select value={month.month()} onChange={(e) => onMonthSelect(month, e.target.value)}>
            {moment.months().map((label, value) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select value={month.year()} onChange={(e) => onYearSelect(month, e.target.value)}>
            {years}
          </select>
        </div>
      </div>
    );
  };

  const isDayBlocked = (day: Moment) => {
    if (minDate && day.isBefore(minDate, 'day')) return true;
    if (maxDate && day.isAfter(maxDate, 'day')) return true;
  };

  const onFocusChange = (focused) => {
    if (keepFocused) return;

    setFocusedInput(focused);
  };

  return (
    <div
      className={classNames('datepicker', {
        'datepicker--36': variant === '36',
        'datepicker--full-width': fullWidth,
      })}
    >
      <DateRangePicker
        startDateId={`start_${id}`}
        endDateId={`end_${id}`}
        onDatesChange={_onChange}
        onFocusChange={onFocusChange}
        startDate={getStartValue()}
        endDate={getEndValue()}
        focusedInput={focusedInput}
        numberOfMonths={isMobile ? 1 : 2}
        hideKeyboardShortcutsPanel
        showDefaultInputIcon
        showClearDates={!hideClearDates}
        isDayBlocked={isDayBlocked}
        startDatePlaceholderText={startDatePlaceholder || ''}
        endDatePlaceholderText={endDatePlaceholder || ''}
        displayFormat="DD MMM YYYY"
        renderMonthElement={renderMonthElement}
        transitionDuration={0}
        isOutsideRange={() => false}
        appendToBody={appendToBody}
        minimumNights={0}
      />
    </div>
  );
};

export default DateInputRange;
